import { useParams } from "react-router-dom";
import styles from "./styles.module.scss";
import contract_icon from "../../assets/icons/contract_icon.svg";
import classnames from "classnames";
import { Checkbox, Label, Tooltip, Stack } from "@altimateai/lego";
import { Card, CardContent } from "@altimateai/lego";
import { Button } from "@altimateai/lego";
import { default as ViolationIcon } from "../../assets/icons/violation_icon.svg?react";
import { default as CodeIcon } from "../../assets/icons/code.svg?react";
import { default as ValidateIcon } from "../../assets/icons/validation_default.svg?react";
import validationProcessing from "../../assets/images/validation_processing.png";
import { default as ValidateSuccessIcon } from "../../assets/icons/validation_success.svg?react";
import { default as ValidateFailIcon } from "../../assets/icons/validation_fail.svg?react";
import { LevelTag, TableTags, type Level } from "../../components/tags";
import { Policy } from "./Policy";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getContractDetail, validateContract, toggleContractSchedule } from "../../helpers/apis";
import { useState, useEffect } from "react";
import { Code } from "./Code";
import { Timeline } from "./Timeline";
import { ComponentLoader } from "../../components/loader";
import cronstrue from "cronstrue";
import { useBreadcrumb } from "@/components/breadcrumbs";
import type { ReactNode } from "react";

const VALIDATION_BUTTON_TIMEOUT = 2000;
const GET_CONTRACT_DETAILS = "GET-CONTRACT-DETAILS";

interface DetailItem {
  label?: string;
  value?: string;
  customElement?: ReactNode;
}

/**
 * remove deprecated flag if used
 * @deprecated
 */
const ContractDetails = () => {
  const [showCode, setShowCode] = useState(false);
  const [isChecked, setIsChecked] = useState(true);
  const [showValidationSuccess, setShowValidationSuccess] = useState(false);
  const [showValidationFail, setShowValidationFail] = useState(false);
  const { contractId } = useParams();
  useBreadcrumb(contractId ?? "Details", {
    defaultParents: [{ label: "Contracts", pathname: "/contracts" }],
  });
  const { data: contract, isLoading } = useQuery({
    queryKey: [GET_CONTRACT_DETAILS, contractId],
    queryFn: () => getContractDetail(contractId!),
    enabled: !!contractId,
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (contract?.name) {
      setIsChecked(contract?.is_scheduled);
    }
  }, [contract]);
  const { mutate: runValidate, isPending: isValidationLoading } = useMutation({
    mutationFn: validateContract,
    onSuccess: res => {
      if (res?.contract_name) {
        setShowValidationSuccess(true);
        setTimeout(() => {
          setShowValidationSuccess(false);
        }, VALIDATION_BUTTON_TIMEOUT);
      } else {
        setShowValidationFail(true);
        setTimeout(() => {
          setShowValidationFail(false);
        }, VALIDATION_BUTTON_TIMEOUT);
      }
    },
    onError: () => {
      setShowValidationFail(true);
      setTimeout(() => {
        setShowValidationFail(false);
      }, VALIDATION_BUTTON_TIMEOUT);
    },
  });

  const { mutate: runToggleSchedule } = useMutation({
    mutationFn: () => toggleContractSchedule(contractId ?? ""),
    onSuccess: res => {
      if (res?.ok) {
        setIsChecked(!isChecked);
      }
    },
  });

  const handleToggleSwitch = () => {
    runToggleSchedule();
  };

  const week_data = contract?.week_data;
  const summary = [
    { label: "Error", value: week_data?.error, color: "level-red" },
    { label: "Warning", value: week_data?.warning, color: "level-orange" },
    { label: "Policies", value: week_data?.policies, color: "level-blue" },
    {
      label: "Validations",
      value: week_data?.validations,
      color: "level-yellow",
    },
  ];
  const details: DetailItem[] = [
    {
      label: "Last updated",
      value: contract?.last_updated,
    },
    { label: "Version", value: contract?.version },
    {
      label: "Created at",
      value: contract?.created_at,
    },
  ];
  if (contract?.slack_channel) details.push({ label: "Slack", value: contract?.slack_channel });
  if (contract?.schedule) {
    let parsedSchedule = "";
    try {
      parsedSchedule = ` (${cronstrue.toString(contract?.schedule)})`;
    } catch (err) {
      /* empty */
    }
    if (parsedSchedule) {
      details.push({
        customElement: (
          <>
            <div className="text-muted">Schedule:</div>
            <div>
              {contract?.schedule} <span className={styles.parsed_schedule}>{parsedSchedule}</span>
            </div>
          </>
        ),
      });
    } else {
      details.push({
        label: "Schedule",
        value: contract?.schedule,
      });
    }
  }
  details.push({
    customElement: (
      <>
        <div className="text-muted">Scheduled By:</div>
        <div className="form-check form-switch">
          <Checkbox
            className="form-check-input"
            role="switch"
            id="flexSwitchCheckDefault"
            checked={isChecked}
            onCheckedChange={handleToggleSwitch}
          />
          <Label
            className={classnames({
              [styles.toggleSwitchText]: isChecked,
              [styles.toggleSwitchTextMuted]: !isChecked,
            })}
            htmlFor="flexSwitchCheckDefault"
          >
            Altimate
          </Label>
        </div>
      </>
    ),
  });
  details.push({ label: "Registry Id", value: contract?.registry_id?.toString() });
  details.push({ label: "Registry Name", value: contract?.registry_name });

  if (isLoading) return <ComponentLoader left={60} label="" />;

  const handleContractValidation = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (contract?.registry_id) {
      runValidate({
        registryId: String(contract.registry_id),
        contractId: contractId!,
      });
    }
  };
  const showValidateIcon = contract?.can_validate && !showValidationFail && !showValidationSuccess;

  return (
    <>
      <div className={styles.contract_details}>
        <Stack align="center" className="mb-4">
          <div
            className={classnames("me-2", styles.icon_bg, {
              [styles.active]: contract?.status === "active",
              [styles.inactive]: contract?.status === "inactive",
            })}
          >
            <img src={contract_icon} alt="" />
          </div>
          <div className={classnames("me-2 fs-3 fw-semibold no-break", styles.header)}>
            {contract?.name}
          </div>
          <Button
            size="sm"
            className="br-3 no-break"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              setShowCode(true);
            }}
          >
            View Code
          </Button>
          {isValidationLoading ? (
            <Stack justify="between" align="center">
              <img
                src={validationProcessing}
                className={styles.validation_processing}
                alt="validationProcessing"
              />
              <div className="ms-1 text-muted fw-400">Processing</div>
            </Stack>
          ) : (
            <>
              {showValidateIcon && (
                <Tooltip
                  title={
                    <>
                      <div>Validate contract</div>
                      {contract?.last_violation_timestamp && (
                        <span className={styles.tooltip_date}>
                          Last&nbsp;:&nbsp;
                          {contract.last_violation_timestamp}
                        </span>
                      )}
                    </>
                  }
                >
                  <ValidateIcon className={styles.validation} onClick={handleContractValidation} />
                </Tooltip>
              )}
              {showValidationSuccess && (
                <Stack align="center" justify="between">
                  <ValidateSuccessIcon className={styles.validation} />
                  <div className="ms-1 text-muted fw-400">Validation Successful</div>
                </Stack>
              )}
              {showValidationFail && (
                <Stack align="center" justify="between">
                  <ValidateFailIcon className={styles.validation} />
                  <div className="ms-1 text-muted fw-400">Validation Failed</div>
                </Stack>
              )}
            </>
          )}
          <div className={styles.spacer} />
          <Card className="me-3 mb-0 no-break" style={{ minWidth: "12em" }}>
            <CardContent className="al-pt-2 al-pb-2 al-pl-4 al-pr-4">
              <Stack align="center" justify="between">
                <Stack align="center" className="me-2 level-red icon-sm">
                  <CodeIcon />
                </Stack>
                <div>{contract?.no_of_violations} Violations</div>
              </Stack>
            </CardContent>
          </Card>
          {contract?.last_violation_timestamp && (
            <Card className="me-3 mb-0 no-break" style={{ minWidth: "12em" }}>
              <CardContent className="al-pt-2 al-pb-2 al-pl-4 al-pr-4">
                <Stack align="center" justify="between">
                  <Stack align="center" className="me-2 level-orange icon-sm">
                    <ViolationIcon />
                  </Stack>
                  <div>{contract.last_violation_timestamp}</div>
                </Stack>
              </CardContent>
            </Card>
          )}
          <Timeline contractId={contractId} />
        </Stack>
        <Stack>
          <Card className={classnames("me-4", styles.description)}>
            <CardContent className="al-pt-4">
              <Stack justify="between" align="center" className="mb-4">
                <div className="fs-5 fw-semibold">Description</div>
                <LevelTag level={contract?.level as Level} />
              </Stack>
              <TableTags tables={contract?.tables ?? []} />
              <div className="mt-3 mb-2 text-muted">{contract?.description}</div>
              <div className="spacer" />
              <Stack justify="between" className="mt-3">
                <div className="fs-5 fw-semibold">State</div>
                <div className="text-muted">Last 7 days facts</div>
              </Stack>
              <div className={classnames("mt-3 mb-3", styles.divider)} />
              <Stack justify="between">
                {summary.map(item => (
                  <Stack key={item.label} direction="column" align="start">
                    <div className={classnames(styles.marker, item.color)} />
                    <div className="fs-4 fw-semibold">{item.value}</div>
                    <div className="text-muted">{item.label}</div>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
          <Card className={styles.details}>
            <CardContent className="al-pt-4">
              <div className="mb-4 fs-5 fw-semibold">Details</div>
              <Stack direction="column" gap="sm">
                <div className={styles.details_row}>
                  <div className="text-muted">Owner:</div>
                  <div>
                    {contract?.owners?.map(item => (
                      <div key={item}>{item}</div>
                    ))}
                  </div>
                </div>
                {details.map((item, index) => (
                  <div key={item.label ?? index} className={styles.details_row}>
                    {/* customElement is to render a custom element */}
                    {!item?.customElement ? (
                      <>
                        <div className="text-muted">{item.label}:</div>
                        <div>{item.value}</div>
                      </>
                    ) : (
                      <>{item?.customElement}</>
                    )}
                  </div>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
        <Policy policies={contract?.policies ?? []} />
      </div>
      <Code isOpen={showCode} toggle={() => setShowCode(b => !b)} contract={contract ?? {}} />
    </>
  );
};

export { ContractDetails };
